import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./AnalogTimePicker.css";

const pad = (value) => String(value).padStart(2, "0");

const POPOVER_WIDTH = 300;
const POPOVER_HEIGHT = 400;
const GAP = 8;
const MARGIN = 8;

export default function AnalogTimePicker({
  value = "22:00",
  onChange,
  label = "Time",
}) {
  const parseTime = (time) => {
    const [h = 22, m = 0] = (time || "22:00")
      .split(":")
      .map(Number);

    return {
      hour: h % 12 || 12,
      minute: m || 0,
      period: h >= 12 ? "PM" : "AM",
    };
  };

  const initial = parseTime(value);

  const [hour, setHour] = useState(initial.hour);
  const [minute, setMinute] = useState(initial.minute);
  const [period, setPeriod] = useState(initial.period);
  const [step, setStep] = useState("hour");
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [ready, setReady] = useState(false);

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const positionPopover = useCallback(() => {
    const trigger = triggerRef.current;
    const pop = popoverRef.current;
    if (!trigger || !pop) return;

    const rect = trigger.getBoundingClientRect();
    const popW = pop.offsetWidth || POPOVER_WIDTH;
    const popH = pop.offsetHeight || POPOVER_HEIGHT;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const placeAbove = spaceBelow < popH + GAP && spaceAbove > spaceBelow;

    let top = placeAbove ? rect.top - popH - GAP : rect.bottom + GAP;
    top = Math.max(MARGIN, Math.min(top, vh - popH - MARGIN));

    let left = rect.left;
    if (left + popW > vw - MARGIN) left = vw - popW - MARGIN;
    left = Math.max(MARGIN, left);

    setPos({ top, left });
  }, []);

  const openPicker = () => {
    setStep("hour");
    setReady(false);
    setOpen(true);
  };

  const closePicker = () => {
    setOpen(false);
    setReady(false);
    setStep("hour");
  };

  useLayoutEffect(() => {
    if (open && !ready) {
      positionPopover();
      setReady(true);
    }
  }, [open, ready, positionPopover]);

  useEffect(() => {
    if (!open) return undefined;

    const handle = () => positionPopover();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);

    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [open, positionPopover]);

  const emitTime = (h, m, p) => {
    const hour24 = (h % 12) + (p === "PM" ? 12 : 0);
    onChange?.(`${pad(hour24)}:${pad(m)}`);
  };

  const selectHour = (h) => {
    setHour(h);
    setStep("minute");
  };

  const selectMinute = (m) => {
    setMinute(m);
    emitTime(hour, m, period);
    closePicker();
  };

  const changePeriod = (p) => {
    setPeriod(p);
    emitTime(hour, minute, p);
  };

  const clockNumbers =
    step === "hour"
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : Array.from({ length: 12 }, (_, i) => i * 5);

  return (
    <div className="analog-time-field">
      <label className="analog-time-label">{label}</label>

      <button
        ref={triggerRef}
        type="button"
        className="analog-time-input"
        onClick={() => (open ? closePicker() : openPicker())}
        aria-expanded={open}
      >
        <span>
          {pad(hour)}:{pad(minute)} {period}
        </span>

        <svg
          viewBox="0 0 24 24"
          width="19"
          height="19"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      </button>

      {open &&
        createPortal(
          <>
            <button
              type="button"
              className="analog-time-backdrop"
              style={{ zIndex: 10060 }}
              aria-label="Close time picker"
              onClick={closePicker}
            />

            <div
              ref={popoverRef}
              className="analog-time-popover"
              style={{
                position: "fixed",
                top: `${pos.top}px`,
                left: `${pos.left}px`,
                right: "auto",
                zIndex: 10061,
                visibility: ready ? "visible" : "hidden",
              }}
            >
              <div className="analog-time-header">
                <div>
                  <span className="analog-time-caption">
                    Select {step === "hour" ? "hour" : "minute"}
                  </span>

                  <div className="analog-time-digital">
                    <button
                      type="button"
                      className={step === "hour" ? "selected" : ""}
                      onClick={() => setStep("hour")}
                    >
                      {pad(hour)}
                    </button>

                    <span>:</span>

                    <button
                      type="button"
                      className={step === "minute" ? "selected" : ""}
                      onClick={() => setStep("minute")}
                    >
                      {pad(minute)}
                    </button>
                  </div>
                </div>

                <div className="analog-time-period">
                  {["AM", "PM"].map((p) => (
                    <button
                      type="button"
                      key={p}
                      className={period === p ? "active" : ""}
                      onClick={() => changePeriod(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="analog-clock">
                <div className="analog-clock-center" />

                {clockNumbers.map((num, index) => {
                  const angle = (index * 30 - 60) * (Math.PI / 180);
                  const x = 50 + 39 * Math.cos(angle);
                  const y = 50 + 39 * Math.sin(angle);

                  const isSelected =
                    step === "hour"
                      ? num === hour
                      : num === minute;

                  return (
                    <button
                      type="button"
                      key={num}
                      className={`analog-clock-number ${
                        isSelected ? "active" : ""
                      }`}
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                      }}
                      onClick={() =>
                        step === "hour"
                          ? selectHour(num)
                          : selectMinute(num)
                      }
                      aria-label={
                        step === "hour"
                          ? `Select hour ${num}`
                          : `Select minute ${pad(num)}`
                      }
                    >
                      {step === "hour" ? num : pad(num)}
                    </button>
                  );
                })}
              </div>

              <div className="analog-time-footer">
                <button
                  type="button"
                  className="analog-time-cancel"
                  onClick={closePicker}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="analog-time-done"
                  onClick={() => {
                    emitTime(hour, minute, period);
                    closePicker();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
